import { useEffect, useState } from "react";
import { ArrowDownRight, Calendar, Filter, Wallet, ArrowLeftRight, RefreshCcw } from "lucide-react";
import { Transaction, Wallet as WalletType } from "../types";
import { ListTransactions } from "../API/services";
import { Modal } from "../../ui/modal";
import Spinner from "../../ui/Spinner/Spinner";
import { useModal } from "../../../hooks/useModal";
import TransactionSummary from "./TransactionSummary";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  wallet: WalletType | null;
}



interface ApiResponse {
    transactions: Transaction[];
    limit: number;
    pageCount: number;
    pageNo: number;
    totalCount: number;
}

const TransactionsModal = ({ isOpen, onClose, wallet }: Props) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);


  const {
  isOpen: isSummaryOpen,
  openModal: openSummaryModal,
  closeModal: closeSummaryModal,
} = useModal();

  useEffect(() => {
    if (isOpen && wallet) {
      fetchTransactions();
    }
  }, [isOpen, wallet, filter]);

  const fetchTransactions = async () => {
    if (!wallet) return;

    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        currency: wallet.currency,
        pageNo: "1",
        limit: "100",
        orderBy: "date",
        order: "desc"
      });

      if (filter !== "all") {
        params.append("status", filter);
      }

      const response = await ListTransactions(params.toString()) as ApiResponse;
      const transactionList = response?.transactions || [];
      setTransactions(transactionList);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to fetch transactions");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  if (!wallet) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatDateMobile = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric"
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "SUCCESSFUL":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "PENDING":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
      case "FAILED":
        return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
      case "UNKNOWN":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
    }
  };

  const getPurposeIcon = (purpose: string) => {
    const pur_pose = purpose?.toLowerCase() || "";
    
    if (pur_pose.includes("fund") || pur_pose.includes("deposit")) {
      return <Wallet className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
    } else if (pur_pose.includes("trade") || pur_pose.includes("settlement")) {
      return <Wallet className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
    } else if (pur_pose.includes("payout") || pur_pose.includes("withdraw")) {
      return <ArrowLeftRight className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
    } else if (pur_pose.includes("transfer")) {
      return <ArrowLeftRight className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
    } else {
      return <Wallet className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
    }
  };

  const getAmountSignAndColor = (transaction: Transaction) => {
    let sign = "";
    let colorClass = "";
    let symbol = transaction.symbol || wallet.symbol;

    if (transaction.status === "SUCCESSFUL") {
      if (transaction.type === "CREDIT" || transaction.type === "RELEASE") {
        sign = "+";
        colorClass = "text-green-600 dark:text-green-400";
      } else if (transaction.type === "DEBIT" || transaction.type === "HOLD") {
        sign = "-";
        colorClass = "text-red-600 dark:text-red-400";
      }
    } else if (transaction.status === "PENDING") {
      colorClass = "text-yellow-600 dark:text-yellow-400";
    }

    return { sign, colorClass, symbol };
  };

  const getDisplayStatus = (status: string) => {
    return status === "UNKNOWN" ? "CANCELLED" : status;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-4xl rounded-2xl border border-gray-200 bg-white p-4
        dark:border-gray-800 dark:bg-gray-900
        sm:p-6"
    >
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {wallet.flag && (
              <img
                src={wallet.flag}
                alt={wallet.currency}
                className="h-5 w-5 sm:h-6 sm:w-6 rounded-full"
              />
            )}
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white/90">
              {wallet.name} Wallet Transactions
            </h2>
          </div>
          
          <button
            onClick={onClose}
            className="rounded-lg p-1 sm:p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            ×
          </button>
        </div>
        
        <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {wallet.symbol} {wallet.balance.toLocaleString()}
          </div>
          
          <div className="flex items-center gap-2">
            <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
            <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              {new Date().toLocaleDateString("en-US", { 
                month: "long",
                year: "numeric" 
              })}
            </span>
          </div>
        </div>

        {/* Filters - Responsive */}
        <div className="mb-4 sm:mb-6 flex gap-1 sm:gap-2 overflow-x-auto pb-2">
          <button
            onClick={() => setFilter("all")}
            className={`flex-shrink-0 rounded-full px-3 py-1 text-xs sm:text-sm font-medium transition-colors ${
              filter === "all"
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
            }`}
          >
            All
          </button>

        </div>
      </div>

      {/* Transactions List */}
      <div className="max-h-[50vh] sm:max-h-[60vh] overflow-y-auto">
        {loading ? (
          <div className="flex h-48 sm:h-64 items-center justify-center">
            <Spinner className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
          </div>
        ) : error ? (
          <div className="flex h-48 sm:h-64 flex-col items-center justify-center text-center p-4">
            <div className="mb-3 sm:mb-4 rounded-full bg-red-100 p-2 sm:p-3 dark:bg-red-900/30">
              <ArrowDownRight className="h-5 w-5 sm:h-6 sm:w-6 text-red-500" />
            </div>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">{error}</p>
            <button
              onClick={fetchTransactions}
              className="rounded-lg bg-primary px-4 py-2 text-sm text-white"
            >
              Retry
            </button>
          </div>
        ) : transactions.length === 0 ? (
          <div className="flex h-48 sm:h-64 flex-col items-center justify-center text-center p-4">
            <div className="mb-3 sm:mb-4 rounded-full bg-gray-100 p-2 sm:p-3 dark:bg-gray-800">
              <Filter className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
            </div>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">No transactions found</p>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
              {filter !== "all" ? "Try changing your filters" : "After your first transaction, you will be able to view it here"}
            </p>
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {transactions.map((transaction) => {
              const { sign, colorClass, symbol } = getAmountSignAndColor(transaction);
              
              return (
                <div
  key={transaction.id}
  onClick={() => {
    setSelectedTransaction(transaction);
    openSummaryModal();
  }}
  className="cursor-pointer rounded-xl border border-gray-200 bg-white p-3 sm:p-4
    hover:shadow-sm dark:border-gray-800 dark:bg-gray-800/50
    transition-all hover:border-primary/20"
>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                      <div className="flex h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0 items-center justify-center rounded-full"
                        style={{
                          backgroundColor: "rgba(22, 41, 143, 0.06)"
                        }}
                      >
                        {getPurposeIcon(transaction.purpose)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm sm:text-base font-medium text-gray-800 dark:text-white/90 truncate">
                          {transaction.purpose || transaction.narration}
                        </h4>
                        
                        {/* Mobile: Stacked layout */}
                        <div className="sm:hidden mt-1 space-y-1">
                          <div className="flex items-center gap-1">
                            <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${getStatusColor(transaction.status)}`}>
                              {getDisplayStatus(transaction.status)}
                            </span>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                              {formatDateMobile(transaction.createdAt)}
                            </span>
                          </div>
                          <div className={`text-sm font-semibold ${colorClass}`}>
                            {sign} {symbol} {Math.abs(transaction.amount).toLocaleString()}
                          </div>
                        </div>
                        
                        {/* Desktop: Inline layout */}
                        <div className="hidden sm:flex mt-2 items-center gap-2">
                          <span className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(transaction.status)}`}>
                            {getDisplayStatus(transaction.status)}
                          </span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {formatDate(transaction.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Desktop: Right side amount */}
                    <div className="hidden sm:block text-right ml-4">
                      <div className={`text-lg font-semibold ${colorClass}`}>
                        {sign} {symbol} {Math.abs(transaction.amount).toLocaleString()}
                      </div>
                      <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {transaction.type}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-t border-gray-200 pt-4 sm:pt-6 dark:border-gray-800">
        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center sm:text-left">
          Showing {transactions.length} of {transactions.length} transactions
        </div>
        <div className="flex items-center justify-center sm:justify-end gap-2">
          <button
            onClick={fetchTransactions}
            className="flex items-center gap-1 sm:gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 sm:px-4 sm:py-2
              text-xs sm:text-sm text-gray-700 hover:bg-gray-100
              dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
          >
            <RefreshCcw className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Refresh</span>
            <span className="sm:hidden">Refresh</span>
          </button>
         
        </div>
      </div>
      <TransactionSummary
  isOpen={isSummaryOpen}
  onClose={closeSummaryModal}
  transaction={selectedTransaction}
  wallet={wallet}
/>
    </Modal>
  );
};

export default TransactionsModal;